package com.iptvplayer.shared.domain.repository

import com.iptvplayer.shared.domain.model.Channel
import com.iptvplayer.shared.domain.model.IptvSource
import com.iptvplayer.shared.domain.model.Playlist
import com.iptvplayer.shared.domain.model.SeriesInfo
import com.iptvplayer.shared.domain.model.SeriesItem
import com.iptvplayer.shared.domain.model.SyncReport
import com.iptvplayer.shared.domain.model.VodItem
import com.iptvplayer.shared.util.Result
import kotlinx.coroutines.flow.Flow

interface ContentRepository {
    suspend fun addSource(source: IptvSource): Result<Unit>
    suspend fun removeSource(sourceId: String): Result<Unit>
    /**
     * Loads the user's persisted sources from the backend and their playlists.
     * Cached after the first call; pass [force] = true (e.g. the Settings
     * "Refresh all" button) to discard the cache and re-fetch everything.
     *
     * Success means the sync *ran* — per-source failures (provider down, bad
     * credentials, network) are reported in the [SyncReport] so the UI can
     * explain missing content instead of silently showing nothing.
     */
    suspend fun syncSources(force: Boolean = false): Result<SyncReport>

    /**
     * Human-readable description of the sync phase in flight ("Syncing My
     * Provider…", "Loading series 3/12 categories…"), null when idle. Lets the
     * UI show *what* is slow instead of a bare spinner.
     */
    val syncStatus: Flow<String?>
    fun getSources(): Flow<List<IptvSource>>
    suspend fun loadPlaylist(source: IptvSource): Result<Playlist>
    fun getChannels(sourceId: String? = null): Flow<List<Channel>>
    fun getVodItems(sourceId: String? = null): Flow<List<VodItem>>
    fun getCategories(sourceId: String? = null): Flow<List<String>>
    fun getChannelsByCategory(category: String): Flow<List<Channel>>
    fun getVodByCategory(category: String): Flow<List<VodItem>>
    fun getSeries(sourceId: String? = null): Flow<List<SeriesItem>>
    fun getSeriesByCategory(category: String): Flow<List<SeriesItem>>
    /** Fetch a specific series' full detail (seasons + episodes) on demand. */
    suspend fun loadSeriesInfo(sourceId: String, seriesId: Int): Result<SeriesInfo>
    /** Refresh the series catalog for all current sources (background; safe to call after [syncSources]). */
    suspend fun refreshSeries(): Result<Unit>
    suspend fun refreshContent(sourceId: String): Result<Unit>

    /**
     * Previous/next live channel for zapping (direction -1 / +1). Prefers
     * channels in the same group as [currentId]; wraps around at the ends.
     * Returns null when the catalog is empty or the channel is unknown.
     */
    suspend fun nextChannel(currentId: String, direction: Int): Channel?

    // ──────────────────── Paged Browse (DB-backed) ────────────────────
    // Reactive windowed reads straight from the local DB: the UI loads one
    // growing window at a time instead of materializing the whole catalog in
    // RAM. A null [category]/[query] disables that filter; passing a [query]
    // searches across all categories. [limit] is the current window size.
    fun browseChannels(category: String?, query: String?, limit: Long): Flow<List<Channel>>
    fun browseChannelCount(category: String?, query: String?): Flow<Long>
    fun channelGroups(): Flow<List<String>>

    fun browseVod(category: String?, query: String?, limit: Long): Flow<List<VodItem>>
    fun browseVodCount(category: String?, query: String?): Flow<Long>
    fun vodGroups(): Flow<List<String>>

    fun browseSeries(category: String?, query: String?, limit: Long): Flow<List<SeriesItem>>
    fun browseSeriesCount(category: String?, query: String?): Flow<Long>

    // One-shot deep pages: fetched once and appended by the paging view model,
    // so scrolling far into a 20k-item catalog never re-reads what's on screen.
    suspend fun pageChannels(category: String?, query: String?, limit: Long, offset: Long): List<Channel>
    suspend fun pageVod(category: String?, query: String?, limit: Long, offset: Long): List<VodItem>
    suspend fun pageSeries(category: String?, query: String?, limit: Long, offset: Long): List<SeriesItem>
    fun seriesGroups(): Flow<List<String>>

    /** Fetch the series catalog once per session (no-op if already loaded). A
     * forced refresh still goes through [refreshSeries]. */
    suspend fun ensureSeriesLoaded(): Result<Unit>
}
